import cx from 'classnames';
import { FC, useMemo } from 'react';

import { useAgencyConstants } from '@core/contexts/ConfigContext';
import { t } from '@core/i18n';
import { WithStyles, withStyles } from '@core/theme/utils/with-styles';
import { Avatar } from '@shared/components/avatar';
import { Badge, BADGE_THEME } from '@shared/components/badge';
import { Ellipsis } from '@shared/components/ellipsis';
import { CloseIcon } from '@shared/icons/close';
import { DragIcon } from '@shared/icons/drag';
import { MixedUser } from '@shared/models/user';
import { InputSize } from '@shared/types/common/input';

import { styles } from './UserLookupCard.styles';

// TODO: The typing of the user is completely wrong. We are trying to accommodate `LookupUserItem` from `listUserToLookupUser`
const getUserImageUrl = (user: MixedUser): string => {
  // Existing MixedUser (Basic or User)
  if ('id' in user) {
    return typeof user.image === 'string' ? user.image : user.image?.url ?? '';
  }
  // New user
  return '';
};

interface StylesConfig {
  avatar: number;
  textContainerDirection: 'row' | 'column';
  text: number;
}

export interface UserLookupCardProps extends WithStyles<typeof styles> {
  disabled?: boolean;
  draggable?: boolean;
  inputSize?: InputSize;
  item: MixedUser;
  showRole?: boolean;
  withContainer?: boolean;
  onRemove?: (user: MixedUser) => void;
  onView?: (id: number) => void;
}

const UserLookupCardComponent: FC<UserLookupCardProps> = ({
  classes,
  disabled,
  draggable,
  inputSize = InputSize.mediumLegacy,
  item,
  showRole = false,
  withContainer = true,
  onRemove,
  onView,
}) => {
  const { constants } = useAgencyConstants();

  const isExisting = 'id' in item;
  const role = 'pivot' in item ? item.pivot?.role : null;
  const roleVisible = role !== null && showRole;

  const stylesConfig: StylesConfig = useMemo(() => {
    switch (inputSize) {
      case InputSize.small:
        return { avatar: 22, textContainerDirection: 'row', text: 13 };
      case InputSize.mediumLegacy:
        return { avatar: 24, textContainerDirection: 'row', text: 13 };
      case InputSize.medium:
        return { avatar: 26, textContainerDirection: 'column', text: 13 };
      case InputSize.large:
        return { avatar: 28, textContainerDirection: 'column', text: 14 };
    }
  }, [inputSize]);

  return (
    <div
      className={cx(classes.root, classes[inputSize], { [classes.rootWithContainer]: withContainer })}
      key={isExisting ? item.id : item.name}
    >
      <div
        className={cx(classes.content, { [classes.contentClickable]: isExisting && onView })}
        onClick={isExisting && onView ? () => onView(item.id) : undefined}
      >
        {!disabled && draggable && <DragIcon classes={{ root: classes.dragIcon }} />}

        {isExisting ? (
          <Avatar
            classes={{ root: classes.avatar }}
            image={getUserImageUrl(item)}
            name={item.name}
            size={stylesConfig.avatar}
            theme={item.theme}
          />
        ) : (
          <Badge text={t('new')} theme={BADGE_THEME.green} classes={{ badge: classes.newBadge }} />
        )}

        <div className={classes.textContainer} style={{ flexDirection: stylesConfig.textContainerDirection }}>
          <Ellipsis
            classes={{ root: classes.text }}
            maxWidth="100%"
            style={{ fontSize: stylesConfig.text }}
            text={item.name}
            withTooltip={false}
          />
          {roleVisible && (
            <Ellipsis
              classes={{ root: classes.subText }}
              maxWidth="100%"
              style={{ fontSize: stylesConfig.text }}
              text={role ? constants.userRoles[role] : t('unknown')}
              withTooltip={false}
            />
          )}
        </div>

        {!disabled && onRemove && (
          <div
            className={classes.closeIconContainer}
            onClick={(e) => {
              e.stopPropagation();
              onRemove(item);
            }}
          >
            <CloseIcon classes={{ root: classes.closeIcon }} />
          </div>
        )}
      </div>
    </div>
  );
};

export const UserLookupCard = withStyles(styles)(UserLookupCardComponent);
